import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { GlobalMessageType, TranslationService } from '@spartacus/core'
import { Order } from '@spartacus/order/root'
import { Observable, Subscription } from 'rxjs'
import { finalize, tap } from 'rxjs/operators'
import { OrderStatus, OrderStatusNotUsedInFE } from '../../../../../models/enum/order.enum'
import { ICustomOrderEntry } from '../../../../../models/interface/order-entry.interface'
import { OrderHistoryService } from '../../../../../services/order-history.service'
import { CustomOrderHistoryService } from '../../../../../services/order/custom-order-history.service'
import { CustomGlobalMessageService } from '../../../../../services/shared/custom-global-message/custom-global-message.service'
import { ConfirmModalComponent, DataConfirmModal } from '../../../../shared/confirm-modal/confirm-modal.component'

@Component({
    selector: 'app-order-details-headline',
    templateUrl: './order-details-headline.component.html',
    styleUrls: ['./order-details-headline.component.scss'],
    standalone: false
})
export class OrderDetailsHeadlineComponent implements OnInit, OnDestroy {
  public spinner = false
  public disableOnCancel = false
  public isCancelling = false
  public tempOrder: Order
  public orderCode: string
  public OrderStatus = OrderStatus
  private subscriptions: Subscription
  public allowCancelOrder: boolean
  order$: Observable<any>
  public orderCodeNew = sessionStorage.getItem('orderCode')

  constructor(
    public orderHistoryService: OrderHistoryService,
    private modalService: NgbModal,
    private changeDetectorRef: ChangeDetectorRef,
    public translationService: TranslationService,
    public globalMessageService: CustomGlobalMessageService,
    public customOrderHistoryService: CustomOrderHistoryService
  ) {}

  ngOnInit(): void {
    const code = JSON.parse(this.orderCodeNew)

    this.order$ = this.customOrderHistoryService.getOrderDetails(code).pipe(
      tap((res) => {
        // check same order
        if (res?.code !== code) {
          return
        }

        // check CANCELLED or CANCELLING order
        if (res?.status === OrderStatus.CANCELLED || res?.status === OrderStatusNotUsedInFE.CANCELLING) {
          return
        }

        // check normal (non cancelled/cancelling) order entry
        if (res?.entries) {
          for (const item of res.entries) {
            if (item.orderEntryStatusDisplay !== 'Cancelling' && item.orderEntryStatusDisplay !== 'Cancelled') {
              this.allowCancelOrder = true
              break
            }
          }
          if (!this.allowCancelOrder) {
            this.setOrderCancel(res)
          }
        }
      })
    )

    this.subscriptions = this.orderHistoryService.itemCancelled$.subscribe((order) => {
      if (this.allItemsAreCancelled(order)) {
        this.setOrderCancel(order)
        this.changeDetectorRef.detectChanges()
      }
    })
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  public onCancelOrder(order: Order): void {
    const dataModal: DataConfirmModal = {
      title: 'Cancel Order',
      msg: 'Are you sure you want to cancel the order?',
      extra: 'This operation can not be undone.',
      yes: 'Cancel Order'
    }

    const modalRef = this.modalService.open(ConfirmModalComponent, { centered: true })
    modalRef.componentInstance.data = dataModal
    modalRef.result.then(
      () => {
        this.spinner = true
        this.disableOnCancel = true
        this.changeDetectorRef.detectChanges()

        this.orderHistoryService
          .cancelFullOrder(order)
          .pipe(
            finalize(() => {
              this.spinner = false
              this.disableOnCancel = false
              this.changeDetectorRef.detectChanges()
            })
          )
          .subscribe(
            () => {
              this.setOrderCancel(order)
              this.globalMessageService.add('Order cancelled successfully.', GlobalMessageType.MSG_TYPE_CONFIRMATION)
              this.orderHistoryService.orderCancelled(order)
            },
            (err) => {
              this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
            }
          )
      },
      () => {}
    )
  }

  public showCancelOrder(order: Order): boolean {
    return (
      this.allowCancelOrder &&
      this.orderHistoryService.checkStatusForFullCancellation(this.isCancelling ? this.tempOrder?.status : order.status)
    )
  }

  private allItemsAreCancelled(order: Order): boolean {
    const entries = order?.entries as Array<ICustomOrderEntry>
    if (entries?.length) {
      for (const item of entries) {
        if (item.orderEntryStatusDisplay !== 'Cancelling' && item.orderEntryStatusDisplay !== 'Cancelled') {
          return false
        }
      }
    }
    return true
  }

  private setOrderCancel(order: Order): void {
    this.tempOrder = { ...order, status: OrderStatusNotUsedInFE.CANCELLING, statusDisplay: 'Cancelling' }
    this.isCancelling = true
    this.allowCancelOrder = false
  }
}
